import type { TripSnapshot } from '@/types'

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

const readError = async (response: Response): Promise<string> => {
    const payload = await response.json().catch(() => null) as { error?: string } | null
    return payload?.error ?? 'Something went wrong. Please try again.'
}

/** Calls one of the trip endpoints from the browser and returns the fresh trip. */
export const tripRequest = async (
    code: string,
    path: '' | 'travelers' | 'expenses' | 'settlements',
    method: Method,
    body?: Record<string, unknown>
): Promise<TripSnapshot> => {
    let response: Response

    try {
        response = await fetch(`/api/trips/${encodeURIComponent(code)}${path ? `/${path}` : ''}`, {
            method,
            headers: body ? { 'Content-Type': 'application/json' } : undefined,
            body: body ? JSON.stringify(body) : undefined
        })
    }
    catch {
        throw new Error('You seem to be offline. Check your connection and try again.')
    }

    if (!response.ok) throw new Error(await readError(response))

    return await response.json() as TripSnapshot
}

export const createTripRequest = async (body: Record<string, unknown>): Promise<string> => {
    let response: Response

    try {
        response = await fetch('/api/trips', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        })
    }
    catch {
        throw new Error('You seem to be offline. Check your connection and try again.')
    }

    if (!response.ok) throw new Error(await readError(response))

    const payload = await response.json() as { shareCode: string }
    return payload.shareCode
}

/** Looks up a live exchange rate: 1 unit of `from` in `to`. Returns null when there is none. */
export const fetchRate = async (from: string, to: string): Promise<{ rate: number, date: string } | null> => {
    try {
        const response = await fetch(`/api/rates?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
        if (!response.ok) return null

        return await response.json() as { rate: number, date: string }
    }
    catch {
        return null
    }
}

const MAX_RECEIPT_SIDE = 1600

const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]+,/, ''))
    reader.onerror = () => reject(new Error('That photo could not be read.'))
    reader.readAsDataURL(blob)
})

/**
 * Shrinks a receipt photo in the browser (longest side 1600px, JPEG) and uploads it.
 * Returns the storage key to save with the expense and a local preview address.
 */
export const uploadReceiptRequest = async (code: string, file: File): Promise<{ key: string, previewUrl: string }> => {
    let bitmap: ImageBitmap

    try {
        bitmap = await createImageBitmap(file)
    }
    catch {
        throw new Error('That photo format is not supported. Try a JPEG or PNG photo.')
    }

    const scale = Math.min(1, MAX_RECEIPT_SIDE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))

    const context = canvas.getContext('2d')
    if (!context) throw new Error('That photo could not be prepared.')

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.82))
    if (!blob) throw new Error('That photo could not be prepared.')

    const data = await blobToBase64(blob)
    let response: Response

    try {
        response = await fetch(`/api/trips/${encodeURIComponent(code)}/receipts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data, contentType: 'image/jpeg' })
        })
    }
    catch {
        throw new Error('You seem to be offline. Check your connection and try again.')
    }

    if (!response.ok) throw new Error(await readError(response))

    const payload = await response.json() as { key: string }

    return { key: payload.key, previewUrl: URL.createObjectURL(blob) }
}

export const receiptUrl = (code: string, expenseId: string): string => {
    return `/api/trips/${encodeURIComponent(code)}/receipts?expense=${encodeURIComponent(expenseId)}`
}

export const deleteTripRequest = async (code: string): Promise<void> => {
    let response: Response

    try {
        response = await fetch(`/api/trips/${encodeURIComponent(code)}`, { method: 'DELETE' })
    }
    catch {
        throw new Error('You seem to be offline. Check your connection and try again.')
    }

    if (!response.ok) throw new Error(await readError(response))
}
